import { Head, Link, usePage } from "@inertiajs/react";
import {
  ArrowLeft,
  BadgeCheck,
  Building2,
  FileText,
  Hash,
  MapPin,
} from "lucide-react";

import CompanyLogoUploader from "@/components/company-logo-uploader";
import InlineEditableField from "@/components/inline-editable-field";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import AppLayout from "@/layouts/app-layout";
import {
  organizationCompaniesPath,
  organizationCompanyPath,
  organizationSectorPath,
} from "@/routes";
import type { BreadcrumbItem, Company, SharedData } from "@/types";

interface CompanyShowProps {
  company: Company;
}

const fiscalStatusOptions = [
  { value: "responsable_inscripto", label: "Responsable Inscripto" },
  { value: "autonomo", label: "Autónomo" },
  { value: "monotributista", label: "Monotributista" },
  { value: "exento", label: "Exento" },
  { value: "no_alcanzado", label: "No Alcanzado" },
];

export default function CompanyShow({ company }: CompanyShowProps) {
  const { auth } = usePage<SharedData>().props;
  const organizationSlug = auth.current_organization?.slug ?? "";
  const companiesHref = organizationCompaniesPath(organizationSlug);
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Compañías", href: companiesHref },
  ];
  const breadcrumbsWithCurrent: BreadcrumbItem[] = [
    ...breadcrumbs,
    { title: company.name, href: "#" },
  ];
  const companyHref = organizationCompanyPath(organizationSlug, company);

  return (
    <AppLayout breadcrumbs={breadcrumbsWithCurrent}>
      <Head title={`Compañía: ${company.name}`} />
      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        <div className="flex flex-col gap-4">
          <Button variant="ghost" size="sm" asChild className="self-start">
            <Link href={companiesHref}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver a Compañías
            </Link>
          </Button>

          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-3xl font-bold">{company.name}</h1>
              {company.legal_name ? (
                <p className="text-muted-foreground">{company.legal_name}</p>
              ) : null}
            </div>

            <Avatar className="h-16 w-16 border shadow-sm">
              <AvatarImage
                src={company.logo_url ?? undefined}
                alt={company.name}
              />
              <AvatarFallback className="text-lg font-semibold">
                {company.name.slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Información de la compañía
            </CardTitle>
            <CardDescription>Haz clic en el lápiz para editar</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InlineEditableField
                label="Nombre comercial"
                value={company.name}
                field="name"
                updateUrl={companyHref}
                icon={FileText}
                placeholder="Nombre de la compañía"
              />

              <InlineEditableField
                label="Razón social"
                value={company.legal_name}
                field="legal_name"
                updateUrl={companyHref}
                icon={FileText}
                placeholder="Razón social"
              />

              <InlineEditableField
                label="CUIT"
                value={company.tax_id}
                field="tax_id"
                updateUrl={companyHref}
                icon={Hash}
                placeholder="Ej: 20-12345678-9"
              />

              <InlineEditableField
                label="Condición fiscal"
                value={company.fiscal_status}
                field="fiscal_status"
                type="select"
                updateUrl={companyHref}
                icon={BadgeCheck}
                options={fiscalStatusOptions}
              />

              <InlineEditableField
                label="Dirección fiscal"
                value={company.fiscal_address}
                field="fiscal_address"
                updateUrl={companyHref}
                icon={MapPin}
                placeholder="Dirección fiscal"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Logo de la compañía
            </CardTitle>
            <CardDescription>Actualiza el logo de la compañía</CardDescription>
          </CardHeader>
          <CardContent>
            <CompanyLogoUploader
              updateUrl={companyHref}
              initialLogoUrl={company.logo_url}
              methodOverride="put"
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Sectores asociados
            </CardTitle>
          </CardHeader>
          <CardContent>
            {company.sectors && company.sectors.length > 0 ? (
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {company.sectors.map((sector) => (
                  <div
                    key={sector.id}
                    className="flex items-center justify-between rounded-lg border p-3 shadow-sm"
                  >
                    <div>
                      <p className="font-semibold">{sector.name}</p>
                      <p className="text-sm text-muted-foreground">
                        Código: {sector.code ?? "—"}
                      </p>
                    </div>
                    <Button variant="outline" size="sm" asChild>
                      <Link href={organizationSectorPath(organizationSlug, sector)}>
                        Ver
                      </Link>
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground">
                No hay sectores asociados todavía.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
